import type { ProposalCollaborationEnvelope } from "work/proposals/negotiation/proposal-collaboration-contract";
import { isReviewerApprovalCurrent } from "work/proposals/negotiation/proposal-reviewer-approval-state";

/**
 * What the submit dialog says when the leader's own reviewers have not finished with what is about to
 * be sent.
 *
 * This warning went out with the review-lock nag (d8016a18e): the leader was told on every submit that
 * a reviewer's approval had gone non-current, by a confirmation that reworded its own question into
 * "do you still want to submit?" and offered no way to resolve it. Submitting is unconditional now and
 * stays that way — this is a line above the change list, not a gate, and the buttons read the same
 * either way. What it restores is agreement: the header's approval badge has gone on counting pending
 * reviews beside the Submit button all along, and a dialog that said nothing while the badge said
 * "2 reviews pending" left the leader to reconcile the two (#5128).
 *
 * "Pending" is therefore the badge's own definition — no CURRENT approval — so the two cannot
 * disagree. A reviewer who approved and then had the proposal edited under them is pending again,
 * because what they approved is not what this submission sends.
 */

/** The viewing party's own reviewers who have no current approval of the state being submitted. */
export function countPendingProposalReviews(envelope: ProposalCollaborationEnvelope): number {
  const reviewers =
    envelope.viewingParty === "Vendor"
      ? envelope.projection.vendorReviewers
      : envelope.projection.clientReviewers;
  // The envelope's approvals, not the projection's `dateApproved`: that field is snapshot metadata
  // which a later submission leaves stale, which is why `applyCurrentReviewerApprovals` exists.
  return reviewers.filter((reviewer) => !isReviewerApprovalCurrent(envelope, reviewer)).length;
}

/**
 * The sentence, or nothing at all when every reviewer has approved this exact state — a proposal with
 * no reviewers included, which has no review to be pending.
 */
export function proposalPendingReviewWarning(
  envelope: ProposalCollaborationEnvelope,
  audienceLabel: string,
): string | undefined {
  const pendingReviews = countPendingProposalReviews(envelope);
  if (pendingReviews === 0) return undefined;

  const reviewers = pendingReviews === 1 ? "1 reviewer has" : `${pendingReviews} reviewers have`;
  return `${reviewers} not approved what you are about to send. Submitting sends it to ${audienceLabel} without their review.`;
}
